import { IconChevronDown, IconX } from '@tabler/icons-solidjs'
import { For, Match, Switch, splitProps } from 'solid-js'
import { pickedFileSize } from '../../lib/file-reference.ts'

export type FileAttachmentLayout = 'rows' | 'tiles' | 'summary'

export interface FileAttachmentListProps {
  files: File[]
  onRemove: (index: number) => void
  disabled?: boolean
  layout?: FileAttachmentLayout
}

const fileSizeFormatter = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 1 })

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${fileSizeFormatter.format(bytes)} Б`
  if (bytes < 1024 ** 2) return `${fileSizeFormatter.format(bytes / 1024)} КБ`
  if (bytes >= 1024 ** 3) return `${fileSizeFormatter.format(bytes / 1024 ** 3)} ГБ`
  return `${fileSizeFormatter.format(bytes / 1024 ** 2)} МБ`
}

const formatFileCount = (count: number): string => {
  const lastTwoDigits = count % 100
  const lastDigit = count % 10
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return `${count} файлів`
  if (lastDigit === 1) return `${count} файл`
  if (lastDigit >= 2 && lastDigit <= 4) return `${count} файли`
  return `${count} файлів`
}

const getFileType = (fileName: string): string => {
  const extension = fileName.split('.').at(-1)
  if (!extension || extension === fileName) return 'ФАЙЛ'
  return extension.slice(0, 4).toLocaleUpperCase('uk-UA')
}

interface RemoveFileButtonProps {
  file: File
  disabled?: boolean
  onClick: () => void
}

const RemoveFileButton = (props: RemoveFileButtonProps) => {
  const [local] = splitProps(props, ['file', 'disabled', 'onClick'])

  return (
    <button
      type="button"
      class="flex size-7 shrink-0 items-center justify-center rounded-control text-text-subtle transition-colors duration-(--duration-press) ease-out hover:bg-surface hover:text-text focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:pointer-events-none disabled:opacity-45"
      disabled={local.disabled}
      aria-label={`Видалити файл ${local.file.name}`}
      onClick={() => local.onClick()}
    >
      <IconX size={16} strokeWidth={1.5} aria-hidden="true" />
    </button>
  )
}

const FileRows = (props: Omit<FileAttachmentListProps, 'layout'>) => (
  <ul class="grid min-w-0 max-w-full gap-1.5">
    <For each={props.files}>
      {(file, index) => (
        <li class="grid min-w-0 max-w-full grid-cols-[2rem_minmax(0,1fr)_auto_auto] items-center gap-2 overflow-hidden rounded-control bg-surface-sunken px-2 py-1.5 text-body">
          <span class="grid size-8 place-items-center rounded-control bg-surface font-mono text-micro font-semibold text-text-muted">
            {getFileType(file.name)}
          </span>
          <span class="min-w-0 truncate text-text" title={file.name}>
            {file.name}
          </span>
          <span class="shrink-0 whitespace-nowrap font-mono text-micro text-text-subtle tabular-nums">
            {formatFileSize(pickedFileSize(file))}
          </span>
          <RemoveFileButton
            file={file}
            disabled={props.disabled}
            onClick={() => props.onRemove(index())}
          />
        </li>
      )}
    </For>
  </ul>
)

const FileTiles = (props: Omit<FileAttachmentListProps, 'layout'>) => (
  <ul class="grid min-w-0 grid-cols-2 gap-2">
    <For each={props.files}>
      {(file, index) => (
        <li class="grid min-w-0 grid-cols-[2rem_minmax(0,1fr)_1.75rem] items-start gap-x-2 rounded-control border border-border bg-surface px-2 py-2">
          <span class="row-span-2 grid size-8 place-items-center rounded-control bg-accent-soft font-mono text-micro font-semibold text-accent">
            {getFileType(file.name)}
          </span>
          <span class="min-w-0 truncate text-body text-text" title={file.name}>
            {file.name}
          </span>
          <span class="row-span-2">
            <RemoveFileButton
              file={file}
              disabled={props.disabled}
              onClick={() => props.onRemove(index())}
            />
          </span>
          <span class="min-w-0 font-mono text-micro text-text-subtle tabular-nums">
            {formatFileSize(pickedFileSize(file))}
          </span>
        </li>
      )}
    </For>
  </ul>
)

export const FileAttachmentList = (props: FileAttachmentListProps) => {
  const [local] = splitProps(props, ['files', 'onRemove', 'disabled', 'layout'])
  const totalSize = () => local.files.reduce((total, file) => total + pickedFileSize(file), 0)

  return (
    <Switch>
      <Match when={local.layout === 'rows'}>
        <FileRows files={local.files} onRemove={local.onRemove} disabled={local.disabled} />
      </Match>

      <Match when={local.layout === 'tiles'}>
        <FileTiles files={local.files} onRemove={local.onRemove} disabled={local.disabled} />
      </Match>

      <Match when={(local.layout ?? 'summary') === 'summary'}>
        <details class="group min-w-0 overflow-hidden rounded-control border border-border bg-surface-sunken">
          <summary class="flex min-h-11 cursor-pointer list-none items-center gap-3 px-3 py-2 marker:hidden">
            <span class="flex -space-x-1" aria-hidden="true">
              <For each={local.files.slice(0, 3)}>
                {(file) => (
                  <span class="grid size-7 place-items-center rounded-pill border-2 border-surface bg-accent-soft font-mono text-[0.5625rem] font-semibold text-accent">
                    {getFileType(file.name)}
                  </span>
                )}
              </For>
            </span>
            <span class="grid min-w-0 flex-1">
              <span class="text-body font-medium text-text">
                {formatFileCount(local.files.length)}
              </span>
              <span class="font-mono text-micro text-text-subtle tabular-nums">
                Загальний розмір: {formatFileSize(totalSize())}
              </span>
            </span>
            <IconChevronDown
              size={16}
              strokeWidth={1.5}
              class="size-4 shrink-0 text-text-subtle transition-transform duration-(--duration-press) group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <div class="border-t border-border p-2">
            <FileRows files={local.files} onRemove={local.onRemove} disabled={local.disabled} />
          </div>
        </details>
      </Match>
    </Switch>
  )
}
